import ExcelJS from "exceljs";
import dayjs from "dayjs";

const saveExcelFile = (buffer, fileName) => {
	const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
	const downloadLink = document.createElement("a");
	downloadLink.href = window.URL.createObjectURL(blob);
	downloadLink.download = fileName;
	downloadLink.click();
};

const toNumber = (value) => {
	const parsed = Number(String(value ?? "").replace(/[^0-9.-]/g, ""));
	return Number.isFinite(parsed) ? parsed : 0;
};

const formatPercent = (value) => {
	const parsed = toNumber(value);
	if (!parsed) return "-";
	return `${parsed.toFixed(2).replace(/\.00$/, "")}%`;
};

const formatRawValue = (value) => {
	if (value === null || value === undefined) return "";
	if (typeof value === "object") return JSON.stringify(value);
	return value;
};

const formatRawHeader = (field) =>
	String(field || "")
		.replace(/_/g, " ")
		.replace(/([a-z])([A-Z])/g, "$1 $2")
		.trim()
		.toUpperCase();

const border = {
	top: { style: "thin" },
	left: { style: "thin" },
	bottom: { style: "thin" },
	right: { style: "thin" },
};

const styleCell = (cell, options = {}) => {
	cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
	cell.border = options.border || border;
	cell.font = { bold: Boolean(options.bold), size: options.size || 8 };
	if (options.fill) {
		cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: options.fill } };
	}
};

const numberFormatKeys = new Set([
	"qty",
	"mrpSales",
	"discount",
	"realisedSales",
	"marginValue",
	"receivable",
	"billingGst",
	"totalReceivable",
	"receivedThroughCard",
	"receivedThroughCash",
	"gv",
	"balanceReceivable",
	"invoiceValue",
	"cndn",
	"salary",
	"incentive",
	"employerEsi",
	"employerPf",
	"totalLiability",
]);

const mainColumns = [
	{ label: "Month", key: "month" },
	{ label: "Store Name", key: "storeName" },
	{ label: "Store Code", key: "sitecode" },
	{ label: "Qty.", key: "qty" },
	{ label: "MRP\nSales", key: "mrpSales" },
	{ label: "Discount", key: "discount" },
	{ label: "Discount\n%", key: "discountPercent", percent: true },
	{ label: "Realised\nSales", key: "realisedSales" },
	{ label: "Margin on\nRealised", key: "marginOnRealised", percent: true },
	{ label: "Margin Value", key: "marginValue" },
	{ label: "Receivabl\ne", key: "receivable" },
	{ label: "Billing\nGST", key: "billingGst" },
	{ label: "Total\nReceivable", key: "totalReceivable" },
	{ label: "Received through\nCard", key: "receivedThroughCard" },
	{ label: "Received through\nCash", key: "receivedThroughCash" },
	{ label: "GV", key: "gv" },
	{ label: "Balance\nReceivable", key: "balanceReceivable" },
	{ label: "Invoice\nValue", key: "invoiceValue" },
	{ label: "CN/DN", key: "cndn" },
];

const writeTable = (sheet, startRow, startCol, columns, rows, includeTotal) => {
	columns.forEach((column, index) => {
		const cell = sheet.getCell(startRow, startCol + index);
		cell.value = column.label;
		styleCell(cell, { bold: true, fill: "EBF1DE", size: 7 });
	});

	rows.forEach((row, rowIndex) => {
		columns.forEach((column, columnIndex) => {
			const cell = sheet.getCell(startRow + rowIndex + 1, startCol + columnIndex);
			cell.value = column.percent ? formatPercent(row[column.key]) : row[column.key] || (numberFormatKeys.has(column.key) ? 0 : "");
			if (numberFormatKeys.has(column.key)) cell.numFmt = "#,##0";
			styleCell(cell);
		});
	});

	if (!includeTotal) return startRow + rows.length + 1;

	const totalRowNumber = startRow + rows.length + 1;
	columns.forEach((column, columnIndex) => {
		const cell = sheet.getCell(totalRowNumber, startCol + columnIndex);
		if (column.key === "storeName") {
			cell.value = "Total";
		} else if (numberFormatKeys.has(column.key)) {
			cell.value = rows.reduce((total, row) => total + toNumber(row[column.key]), 0);
			cell.numFmt = "#,##0";
		} else if (column.key === "discountPercent") {
			const discount = rows.reduce((total, row) => total + toNumber(row.discount), 0);
			const mrpSales = rows.reduce((total, row) => total + toNumber(row.mrpSales), 0);
			cell.value = formatPercent(mrpSales ? (discount / mrpSales) * 100 : 0);
		} else {
			cell.value = column.key === "month" || column.key === "city" || column.key === "marginOnRealised" ? "-" : "";
		}
		styleCell(cell, { bold: true, fill: "DAEEF3" });
	});

	return totalRowNumber + 1;
};

const writeRawDataSheet = (workbook, rawRows = []) => {
	const sheet = workbook.addWorksheet("Raw Data");
	const columns = Array.from(new Set(rawRows.flatMap((row) => Object.keys(row || {}).filter((key) => key !== "id"))));

	columns.forEach((column, index) => {
		const cell = sheet.getCell(1, index + 1);
		cell.value = formatRawHeader(column);
		styleCell(cell, { bold: true, fill: "EBF1DE", size: 8 });
	});

	rawRows.forEach((row, rowIndex) => {
		columns.forEach((column, columnIndex) => {
			const cell = sheet.getCell(rowIndex + 2, columnIndex + 1);
			cell.value = formatRawValue(row[column]);
			styleCell(cell, { size: 8 });
		});
	});

	sheet.columns.forEach((column) => {
		column.width = 16;
	});
};

const normalizeText = (value = "") => String(value || "").toLowerCase().replace(/[^a-z0-9]/g, "");

const isAjantaFranchise = (franchise) => normalizeText(franchise).includes("ajanta");

const formatIndianNumber = (value) => {
	const parsed = toNumber(value);
	return Number.isFinite(parsed) ? parsed.toLocaleString("en-IN") : "";
};

const formatRowMonth = (value) => {
	const parsed = dayjs(value, ["MMM-YY", "MMM YYYY", "MMMM-YYYY", "MMM-YYYY"], true);
	if (parsed.isValid()) return parsed.format("MMM-YY");
	const fallback = dayjs(value);
	return fallback.isValid() ? fallback.format("MMM-YY") : String(value || "");
};

const formatDownloadMonthRange = (fromDate, toDate) => {
	const fromLabel = dayjs(fromDate).isValid() ? dayjs(fromDate).format("MMM-YYYY") : "unknown-month";
	const toLabel = dayjs(toDate).isValid() ? dayjs(toDate).format("MMM-YYYY") : "unknown-month";
	return dayjs(fromDate).isValid() && dayjs(toDate).isValid() && dayjs(fromDate).isSame(dayjs(toDate), "month")
		? fromLabel
		: `${fromLabel}_to_${toLabel}`;
};

const buildDownloadFileName = (franchise, suffix, fromDate, toDate) => {
	const safeFranchise = String(franchise || "fofo").replace(/[^a-z0-9_-]/gi, "_");
	const safeMonthRange = String(formatDownloadMonthRange(fromDate, toDate)).replace(/[^a-z0-9_-]/gi, "_");
	return `${safeFranchise}_${safeMonthRange}_${suffix}.xlsx`;
};

const getAjantaSummaryRows = (rows = []) =>
	rows.map((row) => {
		const realisedSales = toNumber(row?.realisedSales ?? row?.realised_sales ?? row?.realisedValue ?? row?.realised_value);
		const realisedSalesGstSource = getFirstDefined(
			row?.gst_value,
			row?.gstValue,
			row?.realisedSalesGst,
			row?.realised_sales_gst,
			row?.realised_gst,
			row?.sales_gst,
			row?.gst_on_realised
		);
		const netSaleSource = getFirstDefined(row?.netSale, row?.net_sale);
		const realisedSalesGst = toNumber(
			realisedSalesGstSource !== undefined && realisedSalesGstSource !== null && realisedSalesGstSource !== ""
				? realisedSalesGstSource
				: netSaleSource !== undefined && netSaleSource !== null && netSaleSource !== ""
					? realisedSales - toNumber(netSaleSource)
					: 0
		);
		const billingValue = toNumber(row?.invoiceValue);
		const totalReceivable = toNumber(row?.totalReceivable ?? row?.total_receivable);
		const netMargin = toNumber(row?.marginValue) - realisedSalesGst;
		const minimumMargin = toNumber(row?.minimumMargin ?? row?.minimum_margin) || 850000;
		const lessWaiver = toNumber(row?.lessWaiver ?? row?.less_waiver ?? row?.waiver);
		const excessCn = minimumMargin - netMargin - lessWaiver;

		return {
			month: formatRowMonth(row?.month),
			storeName: row?.storeName ?? row?.store_name ?? "",
			storeCode: getFirstDefined(row?.site_code, row?.sitecode, row?.store_code, row?.storeCode, row?.siteCode, ""),
			qty: toNumber(row?.qty),
			mrpSales: toNumber(row?.mrpSales),
			discount: toNumber(row?.discount),
			discountPercent: toNumber(row?.discountPercent),
			realisedSales: realisedSales,
			realisedSalesGst,
			netSale: realisedSales - realisedSalesGst,
			marginOnRealised: row?.marginOnRealised ?? row?.payment_margin_percent ?? "",
			marginValue: toNumber(row?.marginValue),
			receivable: toNumber(row?.receivable),
			billingGst: toNumber(row?.billingGst),
			totalReceivable,
			receivedThroughCard: toNumber(row?.receivedThroughCard),
			gv: toNumber(row?.gv),
			balanceReceivable: toNumber(row?.balanceReceivable),
			billingValue,
			cndn: toNumber(row?.cndn || (totalReceivable - billingValue)),
			minimumMargin,
			lessWaiver,
			netMargin,
			excessCn,
		};
	});

const getFirstDefined = (...values) => values.find((value) => value !== undefined && value !== null && value !== "");

const ajantaMainColumns = [
	{ label: "Month", key: "month" },
	{ label: "Store Name", key: "storeName" },
	{ label: "Store Code", key: "storeCode" },
	{ label: "Qty.", key: "qty", number: true },
	{ label: "MRP Sales", key: "mrpSales", number: true },
	{ label: "Discount", key: "discount", number: true },
	{ label: "Discount %", key: "discountPercent", percent: true },
	{ label: "Realised Sales", key: "realisedSales", number: true },
	{ label: "Realised Sales-GST", key: "realisedSalesGst", number: true },
	{ label: "Net Sale", key: "netSale", number: true },
	{ label: "Margin on Realised", key: "marginOnRealised", percent: true },
	{ label: "Margin Value", key: "marginValue", number: true },
	{ label: "Receivable", key: "receivable", number: true },
	{ label: "P-Billing GST", key: "billingGst", number: true },
	{ label: "Total Receivable", key: "totalReceivable", number: true },
	{ label: "Received through Card", key: "receivedThroughCard", number: true },
	{ label: "GV", key: "gv", number: true, dashOnZero: true },
	{ label: "Balance Receivable", key: "balanceReceivable", number: true },
	{ label: "Billing Value", key: "billingValue", number: true },
	{ label: "CN/DN", key: "cndn", number: true },
];

const ajantaSecondColumns = [
	{ label: "Month", key: "month" },
	{ label: "Store Name", key: "storeName" },
	{ label: "Store Code", key: "storeCode" },
	{ label: "Gross Margin", key: "marginValue", number: true },
	{ label: "Less GST", key: "realisedSalesGst", number: true },
	{ label: "Net Margin", key: "netMargin", number: true },
	{ label: "Minimum Margin", key: "minimumMargin", number: true },
	{ label: "Less Waiver", key: "lessWaiver", number: true, dashOnZero: true },
	{ label: "Excess CN", key: "excessCn", number: true },
];

const styleBand = (sheet, startRow, endRow, startCol, endCol, options = {}) => {
	for (let row = startRow; row <= endRow; row += 1) {
		for (let col = startCol; col <= endCol; col += 1) {
			const cell = sheet.getCell(row, col);
			cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
			cell.font = { bold: Boolean(options.bold), size: options.size || 8 };
			cell.fill = options.fill ? { type: "pattern", pattern: "solid", fgColor: { argb: options.fill } } : cell.fill;
			cell.border = {
				top: row === startRow ? { style: "thin" } : cell.border?.top,
				left: col === startCol ? { style: "thin" } : cell.border?.left,
				bottom: row === endRow ? { style: "thin" } : cell.border?.bottom,
				right: col === endCol ? { style: "thin" } : cell.border?.right,
			};
		}
	}
};

const writeAjantaRow = (sheet, rowNumber, startCol, columns, row, isTotal = false) => {
	columns.forEach((column, index) => {
		const cell = sheet.getCell(rowNumber, startCol + index);
		let value = row?.[column.key];

		if (column.key === "month") value = row?.month ? formatRowMonth(row.month) : "";
		if (column.key === "marginOnRealised" && typeof value === "string" && value.trim()) {
			cell.value = value;
		} else if (column.percent) {
			cell.value = value !== undefined && value !== null && value !== "" ? formatPercent(value) : "";
		} else if (column.key === "gv" || column.dashOnZero) {
			cell.value = toNumber(value) === 0 ? "-" : formatIndianNumber(value);
		} else if (column.number) {
			cell.value = formatIndianNumber(value);
		} else {
			cell.value = value ?? "";
		}

		cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
		cell.font = { bold: true, size: 8 };
		cell.border = {
			top: { style: "thin" },
			left: { style: "thin" },
			bottom: { style: "thin" },
			right: { style: "thin" },
		};
		if (isTotal) cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "DAEEF3" } };
		else if (column.percent) cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "EBF1DE" } };
	});
};

const writeAjantaSummary = async ({ workbook, rows = [], fromDate, toDate, rawRows = [] }) => {
	const sheet = workbook.addWorksheet("Summary");
	const startCol = 2;
	const mainEndCol = startCol + ajantaMainColumns.length - 1;
	const secondEndCol = startCol + ajantaSecondColumns.length - 1;
	const title = "Ajanta Espresso - Payment Working";
	const monthTitle = dayjs(fromDate).isSame(dayjs(toDate), "month")
		? `Month ${dayjs(fromDate).format("MMMM-YYYY")}`
		: `Month ${dayjs(fromDate).format("MMMM-YYYY")} to ${dayjs(toDate).format("MMMM-YYYY")}`;
	const summaryRows = getAjantaSummaryRows(rows);
	const selectedMonthCount = Math.max(new Set(summaryRows.map((row) => row.month).filter(Boolean)).size, summaryRows.length || 0);
	const totalMinimumMargin = 850000 * (selectedMonthCount || 1);
	const ajantaTotal = summaryRows.reduce(
		(acc, row) => ({
			marginValue: acc.marginValue + toNumber(row.marginValue),
			realisedSalesGst: acc.realisedSalesGst + toNumber(row.realisedSalesGst),
			netMargin: acc.netMargin + toNumber(row.netMargin),
			lessWaiver: acc.lessWaiver + toNumber(row.lessWaiver),
			excessCn: acc.excessCn + toNumber(row.excessCn),
		}),
		{ marginValue: 0, realisedSalesGst: 0, netMargin: 0, lessWaiver: 0, excessCn: 0 }
	);
	const preparedBy = getFirstDefined(rawRows[0]?.prepared_by, rawRows[0]?.preparedBy, rawRows[0]?.created_by, rawRows[0]?.createdBy) || "";
	const approvedBy1 = getFirstDefined(rawRows[0]?.approved_by_1, rawRows[0]?.approvedBy1, rawRows[0]?.approved_by, rawRows[0]?.approvedBy) || "";
	const approvedBy2 = getFirstDefined(rawRows[0]?.approved_by_2, rawRows[0]?.approvedBy2) || "";
	const cnReference = getFirstDefined(rawRows[0]?.cn_reference, rawRows[0]?.cnReference, rawRows[0]?.cn_no, rawRows[0]?.cnNo, rawRows[0]?.reference) || "";

	sheet.mergeCells(1, startCol, 1, mainEndCol);
	sheet.mergeCells(2, startCol, 2, mainEndCol);
	sheet.getCell(1, startCol).value = title;
	sheet.getCell(2, startCol).value = monthTitle;
	sheet.getCell(1, startCol).alignment = { vertical: "middle", horizontal: "center" };
	sheet.getCell(2, startCol).alignment = { vertical: "middle", horizontal: "center" };
	sheet.getCell(1, startCol).font = { bold: true, size: 16 };
	sheet.getCell(2, startCol).font = { bold: true, size: 12 };
	sheet.getCell(1, startCol).fill = { type: "pattern", pattern: "solid", fgColor: { argb: "B8CCE4" } };
	sheet.getCell(2, startCol).fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFFF" } };
	styleBand(sheet, 1, 1, startCol, mainEndCol, { fill: "B8CCE4", bold: true, size: 16 });
	styleBand(sheet, 2, 2, startCol, mainEndCol, { bold: true, size: 12 });

	const mainHeaderRow = 4;
	ajantaMainColumns.forEach((column, index) => {
		const cell = sheet.getCell(mainHeaderRow, startCol + index);
		cell.value = column.label;
		cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
		cell.font = { bold: true, size: 8 };
		cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "EBF1DE" } };
		cell.border = { top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" } };
	});

	summaryRows.forEach((row, index) => {
		writeAjantaRow(sheet, mainHeaderRow + 1 + index, startCol, ajantaMainColumns, row);
	});

	const mainTotalRow = mainHeaderRow + 1 + summaryRows.length;
	const mainTotals = summaryRows.reduce(
		(acc, row) => ({
			qty: acc.qty + toNumber(row.qty),
			mrpSales: acc.mrpSales + toNumber(row.mrpSales),
			discount: acc.discount + toNumber(row.discount),
			realisedSales: acc.realisedSales + toNumber(row.realisedSales),
			realisedSalesGst: acc.realisedSalesGst + toNumber(row.realisedSalesGst),
			netSale: acc.netSale + toNumber(row.netSale),
			marginValue: acc.marginValue + toNumber(row.marginValue),
			receivable: acc.receivable + toNumber(row.receivable),
			billingGst: acc.billingGst + toNumber(row.billingGst),
			totalReceivable: acc.totalReceivable + toNumber(row.totalReceivable),
			receivedThroughCard: acc.receivedThroughCard + toNumber(row.receivedThroughCard),
			gv: acc.gv + toNumber(row.gv),
			balanceReceivable: acc.balanceReceivable + toNumber(row.balanceReceivable),
			billingValue: acc.billingValue + toNumber(row.billingValue),
			cndn: acc.cndn + toNumber(row.cndn),
		}),
		{
			qty: 0,
			mrpSales: 0,
			discount: 0,
			realisedSales: 0,
			realisedSalesGst: 0,
			netSale: 0,
			marginValue: 0,
			receivable: 0,
			billingGst: 0,
			totalReceivable: 0,
			receivedThroughCard: 0,
			gv: 0,
			balanceReceivable: 0,
			billingValue: 0,
			cndn: 0,
		}
	);
	const mainTotalPercent = mainTotals.mrpSales ? (mainTotals.discount / mainTotals.mrpSales) * 100 : 0;
	const mainTotalRowData = {
		month: "",
		storeName: "Total",
		storeCode: "",
		qty: mainTotals.qty,
		mrpSales: mainTotals.mrpSales,
		discount: mainTotals.discount,
		discountPercent: mainTotalPercent,
		realisedSales: mainTotals.realisedSales,
		realisedSalesGst: mainTotals.realisedSalesGst,
		netSale: mainTotals.netSale,
		marginOnRealised: "",
		marginValue: mainTotals.marginValue,
		receivable: mainTotals.receivable,
		billingGst: mainTotals.billingGst,
		totalReceivable: mainTotals.totalReceivable,
		receivedThroughCard: mainTotals.receivedThroughCard,
		gv: mainTotals.gv,
		balanceReceivable: mainTotals.balanceReceivable,
		billingValue: mainTotals.billingValue,
		cndn: mainTotals.cndn,
	};

	writeAjantaRow(sheet, mainTotalRow, startCol, ajantaMainColumns, mainTotalRowData, true);

	const secondHeaderRow = mainTotalRow + 2;
	ajantaSecondColumns.forEach((column, index) => {
		const cell = sheet.getCell(secondHeaderRow, startCol + index);
		cell.value = column.label;
		cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
		cell.font = { bold: true, size: 8 };
		cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "EBF1DE" } };
		cell.border = { top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" } };
	});

	const secondDataRow = secondHeaderRow + 1;
	summaryRows.forEach((row, index) => {
		writeAjantaRow(
			sheet,
			secondDataRow + index,
			startCol,
			ajantaSecondColumns,
			{
				month: row.month || "",
				storeName: row.storeName || "",
				storeCode: row.storeCode || "",
				marginValue: row.marginValue || 0,
				realisedSalesGst: row.realisedSalesGst || 0,
				netMargin: row.netMargin || 0,
				minimumMargin: row.minimumMargin || 850000,
				lessWaiver: row.lessWaiver || "-",
				excessCn: row.excessCn || 0,
			}
		);
	});

	const secondTotalRow = secondDataRow + summaryRows.length;
	writeAjantaRow(
		sheet,
		secondTotalRow,
		startCol,
		ajantaSecondColumns,
		{
			month: "",
			storeName: "Total",
			storeCode: "",
			marginValue: ajantaTotal.marginValue,
			realisedSalesGst: ajantaTotal.realisedSalesGst,
			netMargin: ajantaTotal.netMargin,
			minimumMargin: totalMinimumMargin,
			lessWaiver: ajantaTotal.lessWaiver,
			excessCn: ajantaTotal.excessCn,
		},
		true
	);

	const remarksHeaderRow = secondTotalRow + 2;
	const remarksStartCol = startCol;
	const remarksRows = [
		["Remarks", "DUE", "REMARKS"],
		["PAYABLE-1", formatIndianNumber(ajantaTotal.netMargin), `MARGIN FOR THE Month ${dayjs(fromDate).format("MMMM-YYYY")}`],
		["PAYABLE-2", formatIndianNumber(ajantaTotal.excessCn), `Expense Reimbursement FOR THE Month ${dayjs(fromDate).format("MMMM-YYYY")}`],
		["NET PAYABLE", formatIndianNumber(totalMinimumMargin), `Total Payable Month ${dayjs(fromDate).format("MMMM-YYYY")}`],
	];

	remarksRows.forEach((row, rowIndex) => {
		row.forEach((value, colIndex) => {
			const cell = sheet.getCell(remarksHeaderRow + rowIndex, remarksStartCol + colIndex);
			cell.value = value;
			cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
			cell.font = { bold: true, size: 8 };
			cell.border = { top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" } };
			if (rowIndex === remarksRows.length - 1) {
				cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF200" } };
			}
		});
	});

	sheet.getCell(secondDataRow, secondEndCol + 1).value = cnReference;
	sheet.getCell(secondDataRow, secondEndCol + 3).value = "Prepared By";
	sheet.getCell(secondDataRow, secondEndCol + 6).value = "Approved By";
	sheet.getCell(secondDataRow, secondEndCol + 9).value = "Approved By";
	sheet.getCell(secondDataRow + 2, secondEndCol + 3).value = preparedBy;
	sheet.getCell(secondDataRow + 2, secondEndCol + 6).value = approvedBy1;
	sheet.getCell(secondDataRow + 2, secondEndCol + 9).value = approvedBy2;

	[secondDataRow, secondDataRow + 2].forEach((row) => {
		[secondEndCol + 1, secondEndCol + 3, secondEndCol + 6, secondEndCol + 9].forEach((col) => {
			sheet.getCell(row, col).alignment = { vertical: "middle", horizontal: "center" };
			sheet.getCell(row, col).font = { size: 8, bold: row === secondDataRow };
		});
	});

	sheet.columns = Array.from({ length: secondEndCol + 10 }, (_, index) => ({
		width: index === 1 ? 14 : index === 2 ? 24 : index === 3 ? 16 : index === 4 ? 12 : 12,
	}));

	const outerEndRow = remarksHeaderRow + remarksRows.length + 2;
	for (let row = 1; row <= outerEndRow; row += 1) {
		for (let col = startCol; col <= secondEndCol + 10; col += 1) {
			const cell = sheet.getCell(row, col);
			const existing = cell.border || {};
			cell.border = {
				top: row === 1 ? { style: "medium" } : existing.top,
				bottom: row === outerEndRow ? { style: "medium" } : existing.bottom,
				left: col === startCol ? { style: "medium" } : existing.left,
				right: col === secondEndCol + 10 ? { style: "medium" } : existing.right,
			};
		}
	}
};

export const downloadEboPaymentWorkingExcel = async ({ rows = [], rawRows = [], franchise, fromDate, toDate }) => {
	const workbook = new ExcelJS.Workbook();
	if (isAjantaFranchise(franchise)) {
		await writeAjantaSummary({ workbook, rows, franchise, fromDate, toDate, rawRows });
		writeRawDataSheet(workbook, rawRows);
		const buffer = await workbook.xlsx.writeBuffer();
		saveExcelFile(buffer, buildDownloadFileName(franchise || "ajanta", "summary", fromDate, toDate));
		return;
	}
	const sheet = workbook.addWorksheet("Payment Working");
	const startCol = 2;
	const endCol = startCol + mainColumns.length - 1;
	const title = `${franchise || "Franchise"} - Payment Working`;
	const monthTitle = dayjs(fromDate).isSame(dayjs(toDate), "month")
		? `Month ${dayjs(fromDate).format("MMMM-YYYY")}`
		: `Month ${dayjs(fromDate).format("MMM-YYYY")} to ${dayjs(toDate).format("MMM-YYYY")}`;

	sheet.mergeCells(1, startCol, 1, endCol);
	sheet.mergeCells(2, startCol, 2, endCol);
	sheet.getCell(1, startCol).value = title;
	sheet.getCell(2, startCol).value = monthTitle;

	for (let row = 1; row <= 2; row += 1) {
		for (let col = startCol; col <= endCol; col += 1) {
			styleCell(sheet.getCell(row, col), { bold: true, fill: row === 1 ? "B8CCE4" : undefined, size: row === 1 ? 10 : 9 });
		}
	}

	const afterMainTable = writeTable(sheet, 4, startCol, mainColumns, rows, true);
	const footerRow = afterMainTable + 3;

	sheet.getCell(footerRow, 3).value = "Prepared by";
	sheet.getCell(footerRow, 8).value = "Approved by";
	sheet.getCell(footerRow, 15).value = "Approved by";
	sheet.getCell(footerRow + 2, 3).value = "Siddesha M T";
	sheet.getCell(footerRow + 2, 8).value = "Bhavish Kumar";
	sheet.getCell(footerRow + 2, 15).value = "Lavish Soni";

	[footerRow, footerRow + 2].forEach((row) => {
		[3, 8, 15].forEach((col) => {
			sheet.getCell(row, col).alignment = { vertical: "middle", horizontal: "center" };
			sheet.getCell(row, col).font = { size: 8 };
		});
	});

	sheet.columns.forEach((column, index) => {
		column.width = index === 2 ? 22 : 11;
	});

	const outerEndRow = footerRow + 4;
	for (let row = 1; row <= outerEndRow; row += 1) {
		for (let col = startCol; col <= endCol; col += 1) {
			const cell = sheet.getCell(row, col);
			const existing = cell.border || {};
			cell.border = {
				...existing,
				top: row === 1 ? { style: "medium" } : existing.top,
				bottom: row === outerEndRow ? { style: "medium" } : existing.bottom,
				left: col === startCol ? { style: "medium" } : existing.left,
				right: col === endCol ? { style: "medium" } : existing.right,
			};
		}
	}

	writeRawDataSheet(workbook, rawRows);

	const buffer = await workbook.xlsx.writeBuffer();
	saveExcelFile(buffer, buildDownloadFileName(franchise || "fofo", "payment-working", fromDate, toDate));
};
